/**
 * CSV Uploader Component
 * Manages CSV file selection and parsing from server files
 */

import { useCallback, useState } from 'react'
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger'
import { FileType } from '../FileTypeSelector'
import { FileSource } from '../FileSourceSelector'
import { DwgCleanModal } from '../DwgCleanModal'
import { useCSVUploadState } from './hooks/useCSVUploadState'
import { useDwgCleanStep } from './hooks/useDwgCleanStep'
import { useLocalUpload } from './hooks/useLocalUpload'
import { useServerDownload } from './hooks/useServerDownload'
import { useLayerSelection } from './hooks/useLayerSelection'
import { CSVSourceSelector } from './components/CSVSourceSelector'
import { CSVStatusView } from './components/CSVStatusView'
import type { CSVUploaderProps } from './types'

export type { CSVUploaderProps }
export type { ViewMode } from './types'

export function CSVUploader({ onMappingRequired, currentFloor, mapId }: CSVUploaderProps = {}) {
  const state = useCSVUploadState(mapId)
  // 빈 층의 idle 은 서버 DXF 목록을 자동으로 연다 — 뒤로 누른 뒤엔 다시 열지 않아야 닫힌다.
  const [isCadListDismissed, setIsCadListDismissed] = useState(false)

  const {
    viewMode, setViewMode,
    selectedFileType, setSelectedFileType,
    uploadState, rawData, parsedData, setFile, setUploadState, parseFile,
    hasObjectTypes, isLoadingTypes, currentProjectId, siteProjectId, currentLot, storeCurrentFloor,
    downloadError, setDownloadError, downloadMessage, setDownloadMessage,
    showLayerModal, setShowLayerModal,
    analyzedLayers, setAnalyzedLayers,
    selectedDxfFile, setSelectedDxfFile,
    handleClear, handleRetry,
  } = state

  const handleDwgProgress = useCallback((message: string, progress: number) => {
    setViewMode('downloading')
    setDownloadMessage(message)
    setUploadState({ status: 'uploading', progress })
  }, [setViewMode, setDownloadMessage, setUploadState])
  const { resolveDwg, dwgModal } = useDwgCleanStep(siteProjectId, handleDwgProgress)

  const { handleLocalFileSelect, handleLocalDXFFileSelect } = useLocalUpload({
    setViewMode, setUploadState, setFile, parseFile,
    setDownloadError, setDownloadMessage,
    setSelectedDxfFile, setAnalyzedLayers, setShowLayerModal,
    onMappingRequired, resolveDwg, currentProjectId, siteProjectId, storeCurrentFloor, currentFloor,
  })

  const { handleServerCSVFileSelect, handleCADFileSelect } = useServerDownload({
    setViewMode, setUploadState, setFile, parseFile,
    setDownloadError, setDownloadMessage,
    setSelectedDxfFile, setAnalyzedLayers, setShowLayerModal,
    onMappingRequired, resolveDwg, currentProjectId, siteProjectId, currentLot, storeCurrentFloor, currentFloor,
  })
  const dwgCleanModal = dwgModal && <DwgCleanModal {...dwgModal} />

  const { handleLayerSelectionConfirm, handleLayerSelectionCancel } = useLayerSelection({
    setViewMode, setUploadState, setFile, parseFile,
    setDownloadError, setDownloadMessage,
    setShowLayerModal, setSelectedDxfFile, setAnalyzedLayers,
    selectedDxfFile, onMappingRequired, currentProjectId, siteProjectId,
  })

  const handleFileTypeSelect = (type: FileType) => {
    log.info(LogCategory.CSV, 'File type selected', { type })
    setSelectedFileType(type)
    setViewMode('source-select')
  }

  const handleCancelFileTypeSelect = () => {
    setViewMode('idle')
    setSelectedFileType(null)
  }

  const handleSourceSelect = (source: FileSource) => {
    log.info(LogCategory.CSV, 'File source selected', { source, fileType: selectedFileType })

    if (!selectedFileType) return

    if (selectedFileType === 'csv') {
      if (source === 'upload') {
        setViewMode('local-upload')
      } else if (source === 'server') {
        setViewMode('server-csv')
      }
    } else if (selectedFileType === 'dxf') {
      if (source === 'upload') {
        setViewMode('local-dxf-upload')
      } else if (source === 'server') {
        setViewMode('server-cad')
      }
    }
  }

  // CSV 선택 화면 제거로 DXF 목록이 진입점이 됨 — 취소 시 뒤로 돌아갈 file-type-select
  // 단계가 없으므로 idle 로 복귀.
  const handleCancelSourceSelect = () => {
    setIsCadListDismissed(true)
    setViewMode('idle')
  }

  // Source selection views (file-type-select, source-select, local-upload, local-dxf-upload, server-csv, server-cad)
  const sourceViewModes = ['file-type-select', 'source-select', 'local-upload', 'local-dxf-upload', 'server-csv', 'server-cad'] as const
  if ((sourceViewModes as readonly string[]).includes(viewMode)) {
    return (
      <>
      <CSVSourceSelector
        viewMode={viewMode}
        selectedFileType={selectedFileType}
        currentProjectId={currentProjectId}
        siteProjectId={siteProjectId}
        showLayerModal={showLayerModal}
        selectedDxfFile={selectedDxfFile}
        analyzedLayers={analyzedLayers}
        onFileTypeSelect={handleFileTypeSelect}
        onCancelFileTypeSelect={handleCancelFileTypeSelect}
        onSourceSelect={handleSourceSelect}
        onCancelSourceSelect={handleCancelSourceSelect}
        onLocalFileSelect={handleLocalFileSelect}
        onLocalDXFFileSelect={handleLocalDXFFileSelect}
        onServerCSVFileSelect={handleServerCSVFileSelect}
        onCADFileSelect={handleCADFileSelect}
        onLayerSelectionConfirm={handleLayerSelectionConfirm}
        onLayerSelectionCancel={handleLayerSelectionCancel}
        onSetViewMode={setViewMode}
      />
      {dwgCleanModal}
      </>
    )
  }

  // Status views (idle, downloading, parsing, error, parsed, fallback)
  return (
    <>
    <CSVStatusView
      viewMode={viewMode}
      uploadState={uploadState}
      rawData={rawData}
      parsedData={parsedData}
      hasObjectTypes={hasObjectTypes}
      isLoadingTypes={isLoadingTypes}
      downloadError={downloadError}
      downloadMessage={downloadMessage}
      showLayerModal={showLayerModal}
      selectedDxfFile={selectedDxfFile}
      analyzedLayers={analyzedLayers}
      siteProjectId={siteProjectId}
      isCadListDismissed={isCadListDismissed}
      onClear={handleClear}
      onRetry={handleRetry}
      onSetViewMode={setViewMode}
      onLayerSelectionConfirm={handleLayerSelectionConfirm}
      onLayerSelectionCancel={handleLayerSelectionCancel}
    />
    {dwgCleanModal}
    </>
  )
}
